import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Workflow } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { formatCompact, formatDuration, formatRelative, toNumber } from "./format";
import { cn } from "../../lib/utils";

function successTone(rate) {
  if (rate >= 95) return "border-emerald-400/30 bg-emerald-500/10 text-emerald-300";
  if (rate >= 80) return "border-amber-400/30 bg-amber-500/10 text-amber-300";
  return "border-rose-400/30 bg-rose-500/10 text-rose-300";
}

/**
 * Workflows ranked by executions, with the outcome of those runs.
 *
 * The success rate here is computed from `workflow_executions.status` over terminal
 * states only. The old dashboard's success rate read `step_execution_results`, a
 * table with zero rows, and was therefore always 0.0% — displayed as if it were a
 * measurement.
 */
export function TopWorkflowsCard({ topWorkflows = [] }) {
  const workflows = (Array.isArray(topWorkflows) ? topWorkflows : []).filter((w) => w?.id);

  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_45px_rgba(0,0,0,0.4)]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-gray-100">
          <Workflow className="h-5 w-5 text-violet-300" />
          Top workflows
        </CardTitle>
        <CardDescription className="text-gray-300">
          Ranked by executions. Success rate and median duration over the period.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-0">
        {workflows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-t border-white/5 bg-white/[0.02]">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Workflow
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Runs
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Failed
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Success
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Median
                  </th>
                </tr>
              </thead>
              <tbody>
                {workflows.map((workflow) => {
                  const rate = toNumber(workflow.success_rate);
                  const failed = toNumber(workflow.failed);
                  return (
                    <tr
                      key={workflow.id}
                      className="border-t border-white/5 transition-colors hover:bg-white/[0.03]"
                    >
                      <td className="max-w-[16rem] px-4 py-3">
                        <p className="truncate text-sm font-medium text-gray-100">
                          {workflow.name || "Untitled workflow"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-white">
                        {formatCompact(workflow.executions)}
                      </td>
                      <td
                        className={cn(
                          "px-4 py-3 text-right text-sm tabular-nums",
                          failed > 0 ? "text-rose-300" : "text-gray-500"
                        )}
                      >
                        {failed > 0 ? formatCompact(failed) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={cn(
                            "inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums",
                            successTone(rate)
                          )}
                        >
                          {rate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-300">
                        {formatDuration(workflow.median_duration_seconds)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-10 text-center text-sm text-gray-400">
            No workflow ran in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The runs that failed, and why.
 *
 * Actionable, and previously nowhere in the product: the old dashboard could tell
 * you a success rate (which was always zero) but never which run broke or what it
 * said on the way out.
 */
export function RecentFailuresCard({ recentFailures = [] }) {
  const failures = (Array.isArray(recentFailures) ? recentFailures : []).filter(
    (f) => f?.execution_id
  );

  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_45px_rgba(0,0,0,0.4)]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-gray-100">
          <AlertTriangle className="h-5 w-5 text-rose-300" />
          Recent failures
        </CardTitle>
        <CardDescription className="text-gray-300">
          Runs that ended in failure during this period.
        </CardDescription>
      </CardHeader>
      {/* Scrolls inside its own box rather than growing the card. Stack traces run
          long, and letting ten of them set the row height left the table beside it
          stranded above a wall of empty black. */}
      <CardContent className="max-h-[26rem] flex-1 space-y-2 overflow-y-auto">
        {failures.length > 0 ? (
          failures.map((failure) => (
            <FailureRow key={failure.execution_id} failure={failure} />
          ))
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400/60" strokeWidth={1.5} />
            <p className="text-sm text-gray-300">No failures in this period.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One failed run. The error is clamped to two lines — these messages are stack
 * traces and nested sub-workflow faults, and shown in full they push everything
 * below them off the screen. Clicking expands the one you care about.
 */
function FailureRow({ failure }) {
  const [expanded, setExpanded] = useState(false);
  const message = failure.error_message;

  return (
    <div className="rounded-xl border border-rose-400/15 bg-rose-500/[0.06] p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 truncate text-sm font-medium text-gray-100">
          {failure.workflow_name || "Untitled workflow"}
        </p>
        <span className="flex-shrink-0 text-xs tabular-nums text-gray-400">
          {formatRelative(failure.failed_at)}
        </span>
      </div>
      {message ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? "Collapse" : "Show full error"}
          className="mt-1 w-full text-left"
        >
          <span
            className={cn(
              "block break-words font-mono text-xs text-rose-200/80",
              !expanded && "line-clamp-2"
            )}
          >
            {message}
          </span>
        </button>
      ) : (
        <p className="mt-1 text-xs italic text-gray-500">
          No error message was recorded.
        </p>
      )}
    </div>
  );
}
